import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kUserFilesApiBasePath } from '@/constants/constants-api-paths'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { InitUploadRequest, InitUploadResponse, InitUploadResponseSchema } from '../schemas'

/**
 * Initialize upload metadata for a batch of files.
 * Must be called before transferring bytes through the API-selected storage transport.
 *
 * @param files - Array of files to initialize
 * @returns Server-generated file_id and storage_path for each file
 * @throws Error when user is not authenticated
 * @throws ApiError when the init-upload request fails
 */
export async function initUploadBatch(files: Array<{ clientTmpId: string; filename: string; folderId: string }>): Promise<InitUploadResponse> {
  const sentryTransactionId = nanoid()

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const request: InitUploadRequest = {
      supports_native_gcs_signed_upload: true,
      files: files.map((f) => ({
        client_tmp_id: f.clientTmpId,
        filename: f.filename,
        folder_id: f.folderId,
      })),
    }

    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kUserFilesApiBasePath + '/init-upload'
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify(request),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: request,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })

      throw new ApiError(response.status, detail || `Init upload failed: ${response.status} ${response.statusText}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Init upload response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: request,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid init upload response JSON')
    }

    const parsed = InitUploadResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Init upload response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: request,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid init upload response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { fileCount: files.length },
        })
      })
    }
    throw error
  }
}
